import React, { ReactNode } from 'react';
import { DatasetType, TimePeriod } from 'app/model/CORE';
import { Flex } from 'app/components/common/Flex';
import SelectEnum from 'app/common/input/SelectEnum';
import { Button, Popover, RadioChangeEvent, Space } from 'antd';
import TimeUtils from 'app/common/util/TimeUtils';
import { LabelDisplayMode, TimePeriodSelector } from 'app/components/map/control/TimePeriodSelector';
import { MonitoringPeriodSettings, TimeOptionEnum } from 'app/model/client/SOL49';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import './GlobalTimeSelectionPopup.less';
import ConditionalRender from 'app/common/components/ConditionalRender';

import AppContext from '../../../../common/context/AppContext';

export interface GlobalTimeSelectionPopupProps {
    monitoringPeriodSettings?: MonitoringPeriodSettings;
    onApply: (settings: MonitoringPeriodSettings) => void;
    children: undefined | ReactNode;
}

interface GlobalTimeSelectionPopupState {
    monitoringPeriodSettings: MonitoringPeriodSettings;
    datasetType: DatasetType;
    open: boolean;
}

export default class GlobalTimeSelectionPopup extends React.PureComponent<
    GlobalTimeSelectionPopupProps,
    GlobalTimeSelectionPopupState
> {
    constructor(props) {
        super(props);
        this.state = {
            monitoringPeriodSettings: Object.assign({}, AppContext.monitoringPeriodSettings),
            open: false,
            datasetType: AppContext.getDataset().type,
        };
    }

    componentDidUpdate(
        prevProps: Readonly<GlobalTimeSelectionPopupProps>,
        prevState: Readonly<GlobalTimeSelectionPopupState>,
        snapshot?: any,
    ): void {
        if (prevState.open === false && this.state.open) {
            this.setState({
                monitoringPeriodSettings: Object.assign({}, AppContext.monitoringPeriodSettings),
            });
        }
    }

    render() {
        return (
            <Popover
                open={this.state.open}
                key="global-time-selection"
                trigger="click"
                placement="bottom"
                content={
                    <GlobalTimeSelectionPopupContent
                        onApply={this.handlerOnApplyClick}
                        onClose={this.handlerOnCloseClick}
                        onMonitoringPeriodSettingsChange={this.handleMonitoringPeriodSettingsChange}
                        monitoringPeriodSettings={this.state.monitoringPeriodSettings}
                        datasetType={this.state.datasetType}
                        onDatasetTypeChange={this.handleDatasetTypeChange}
                    />
                }>
                {this.props.children}
            </Popover>
        );
    }

    private handleDatasetTypeChange = (datasetType: DatasetType) => {
        if (datasetType === DatasetType.OPERATIONAL) {
            this.setState({
                datasetType,
                monitoringPeriodSettings: { ...this.state.monitoringPeriodSettings, deltaDay: 0 },
            });
        } else {
            this.setState({
                datasetType,
                monitoringPeriodSettings: { ...this.state.monitoringPeriodSettings, deltaDay: 1 },
            });
        }
    };

    public show() {
        this.setState({
            open: true,
        });
    }

    private hide() {
        this.setState({
            open: false,
        });
    }

    handlerOnApplyClick = () => {
        this.props.onApply(this.state.monitoringPeriodSettings);
        this.hide();
    };

    handlerOnCloseClick = () => {
        this.hide();
    };

    handleMonitoringPeriodSettingsChange = (monitoringPeriodSettings: MonitoringPeriodSettings): void => {
        let newDataset = this.state.datasetType;
        if (monitoringPeriodSettings.deltaDay > 0 && this.state.datasetType === DatasetType.OPERATIONAL) {
            newDataset = DatasetType.FORECAST;
        } else if (monitoringPeriodSettings.deltaDay < 1 && this.state.datasetType === DatasetType.FORECAST) {
            newDataset = DatasetType.OPERATIONAL;
        }
        this.setState({ monitoringPeriodSettings, datasetType: newDataset });
    };
}

interface GlobalTimeSelectionPopupContentProps {
    monitoringPeriodSettings: MonitoringPeriodSettings;
    onApply: () => void;
    onClose: () => void;
    onMonitoringPeriodSettingsChange?: (settings: MonitoringPeriodSettings) => void;
    datasetType: DatasetType;
    onDatasetTypeChange: (type: DatasetType) => void;
}

class GlobalTimeSelectionPopupContent extends React.PureComponent<GlobalTimeSelectionPopupContentProps> {
    render() {
        const monitoredDay = TimeUtils.addDays(
            TimeUtils.withoutTimeOfDay(TimeUtils.NOW()),
            this.props.monitoringPeriodSettings.deltaDay,
        );
        const refTime = TimeUtils.mergeDateAndTime(monitoredDay, TimeUtils.NOW());
        const timePeriodRange = {
            from: monitoredDay,
            to: TimeUtils.addDays(monitoredDay, 1),
        };
        let timePeriod: TimePeriod =
            this.props.monitoringPeriodSettings.mode === TimeOptionEnum.RELATIVE_TO_NOW
                ? {
                      from: TimeUtils.addMinutes(refTime, -1 * this.props.monitoringPeriodSettings.beforeCurrentMn),
                      to: TimeUtils.addMinutes(refTime, this.props.monitoringPeriodSettings.afterCurrentMn),
                  }
                : {
                      from: TimeUtils.addMillis(monitoredDay, this.props.monitoringPeriodSettings.fromMs),
                      to: TimeUtils.addMillis(monitoredDay, this.props.monitoringPeriodSettings.toMs),
                  };
        // Make sure the TimePeriod is inside the TimePeriodRange (in case it has been loaded from local storage)
        timePeriod = TimePeriodUtils.intersection(timePeriod, timePeriodRange);
        const isOperational = this.props.datasetType === DatasetType.OPERATIONAL;
        return (
            <Flex
                direction="vertical"
                className="time-selection-popup settings-content"
                style={{ width: '38em', padding: '0 3em' }}>
                <Flex direction="horizontal">
                    <SelectEnum
                        enumType={TimeOptionEnum}
                        displayNameMap={TimeOptionEnumDisplayMap}
                        value={this.props.monitoringPeriodSettings.mode}
                        onChange={(mode: TimeOptionEnum) =>
                            this.handleTimeDisplayModeChange(mode, refTime, monitoredDay)
                        }
                    />
                    <ConditionalRender condition={this.props.monitoringPeriodSettings.mode === TimeOptionEnum.ABSOLUTE}>
                        <Button
                            style={{ marginLeft: '1em' }}
                            onClick={() =>
                                this.props.onMonitoringPeriodSettingsChange({
                                    ...this.props.monitoringPeriodSettings,
                                    fromMs: 0,
                                    toMs: TimeUtils.DAY_TO_MS,
                                })
                            }>
                            ALL DAY
                        </Button>
                    </ConditionalRender>
                </Flex>
                <Flex style={{ marginTop: '1em', marginBottom: '1em' }}>
                    <TimePeriodSelector
                        width="100%"
                        value={timePeriod}
                        timePeriodRange={timePeriodRange}
                        labelDisplayMode={this.toLabelDisplayMode(this.props.monitoringPeriodSettings.mode)}
                        currentTime={refTime}
                        markIntervalMs={60 * TimeUtils.MIN_TO_MS}
                        showCurrentTime={true}
                        currentTimeLabel="NOW"
                        onChange={this.handleTimePeriodChange(refTime)}
                    />
                </Flex>
                <Flex style={{ justifyContent: 'right' }}>
                    <Space>
                        <Button className={'largeButton'} onClick={this.handleClose}>
                            Cancel
                        </Button>
                        <Button
                            className={`largeButton ${isOperational ? 'blueButton' : 'greenButton'}`}
                            onClick={this.handleApply}>
                            Apply
                        </Button>
                    </Space>
                </Flex>
            </Flex>
        );
    }

    private handleTimeDisplayModeChange = (mode: TimeOptionEnum, refTime: Date, monitoredDay: Date) => {
        let newSettings: MonitoringPeriodSettings;
        if (mode === TimeOptionEnum.RELATIVE_TO_NOW) {
            // Convert absolute to relative
            const beforeCurrentMn = TimeUtils.diffInMinutes(
                refTime,
                TimeUtils.addMillis(monitoredDay, this.props.monitoringPeriodSettings.fromMs),
            );
            const afterCurrentMn = TimeUtils.diffInMinutes(
                TimeUtils.addMillis(monitoredDay, this.props.monitoringPeriodSettings.toMs),
                refTime,
            );
            newSettings = { ...this.props.monitoringPeriodSettings, mode, beforeCurrentMn, afterCurrentMn };
        } else {
            // Convert relative to absolute date
            const fromMs = TimeUtils.timeSinceMidnightInMs(
                TimeUtils.addMinutes(refTime, -this.props.monitoringPeriodSettings.beforeCurrentMn),
            );
            const toMs = TimeUtils.timeSinceMidnightInMs(
                TimeUtils.addMinutes(refTime, this.props.monitoringPeriodSettings.afterCurrentMn),
            );
            newSettings = { ...this.props.monitoringPeriodSettings, mode, fromMs, toMs };
        }
        this.props.onMonitoringPeriodSettingsChange(newSettings);
    };

    handleMonitoredDayChange = (deltaDay: number) => {
        this.props.onMonitoringPeriodSettingsChange({ ...this.props.monitoringPeriodSettings, deltaDay });
    };

    handleDatasetChange = (event: RadioChangeEvent) => {
        this.props.onDatasetTypeChange(
            event.target.value === 'OPERATIONAL' ? DatasetType.OPERATIONAL : DatasetType.FORECAST,
        );
    };

    handleApply = () => {
        // event.stopPropagation();
        this.props.onApply();
    };
    handleClose = () => {
        // event.stopPropagation();
        this.props.onClose();
    };
    handleTimePeriodChange = (refTime: Date) => (timePeriod: TimePeriod) => {
        if (this.props.monitoringPeriodSettings.mode === TimeOptionEnum.RELATIVE_TO_NOW) {
            //adjust to hours
            const adjustedTimePeriod = round(timePeriod, refTime);
            const settings = {
                ...this.props.monitoringPeriodSettings,
                beforeCurrentMn: -1 * TimeUtils.diffInMinutes(adjustedTimePeriod.from, refTime),
                afterCurrentMn: TimeUtils.diffInMinutes(adjustedTimePeriod.to, refTime),
            };
            this.props.onMonitoringPeriodSettingsChange(settings);
        } else {
            const fromMs = TimeUtils.timeSinceMidnightInMs(timePeriod.from);
            const toMs = TimeUtils.timeSinceMidnightInMs(timePeriod.to);
            this.props.onMonitoringPeriodSettingsChange({ ...this.props.monitoringPeriodSettings, fromMs, toMs });
        }
    };

    private toLabelDisplayMode(timeOption: TimeOptionEnum): LabelDisplayMode {
        switch (timeOption) {
            case TimeOptionEnum.RELATIVE_TO_NOW:
                return LabelDisplayMode.RELATIVE_TO_NOW;
            case TimeOptionEnum.ABSOLUTE:
                return LabelDisplayMode.ABSOLUTE;
        }
    }
}

const TimeOptionEnumDisplayMap = new Map<TimeOptionEnum, string>([
    [TimeOptionEnum.RELATIVE_TO_NOW, 'Relative to now'],
    [TimeOptionEnum.ABSOLUTE, 'Absolute'],
]);

export function round(timePeriod: TimePeriod, currentTime: Date) {
    const fromHours = Math.round(TimeUtils.diffInMinutesUpper(timePeriod.from, currentTime) / 60);
    const toHours = Math.round(TimeUtils.diffInMinutesUpper(timePeriod.to, currentTime) / 60);
    return {
        from: TimeUtils.addMinutes(currentTime, fromHours * 60),
        to: TimeUtils.addMinutes(currentTime, toHours * 60),
    };
}
